import { useEffect, useState } from "react";

import { adminApi, type Stats } from "@/api/admin";
import { ApiError } from "@/api/client";

export default function StatsPage() {
  const [data, setData] = useState<Stats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminApi
      .stats()
      .then(setData)
      .catch((e: unknown) =>
        setError(e instanceof ApiError ? e.message : String(e)),
      );
  }, []);

  if (error)
    return (
      <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
        {error}
      </div>
    );
  if (!data)
    return <div className="py-6 text-sm text-slate-500">加载中…</div>;

  const maxDaily = Math.max(
    1,
    ...data.daily_favorites_last_7d.map((d) => d.count),
  );
  const maxMsg = Math.max(
    1,
    ...Object.values(data.msg_type_distribution),
  );

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-4">
        <Counter label="用户" value={data.totals.users} />
        <Counter label="收藏" value={data.totals.favorites} />
        <Counter label="共享" value={data.totals.shares} />
        <Counter label="分组" value={data.totals.groups} />
      </div>

      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="mb-3 text-sm font-semibold">近 7 日新增收藏</h3>
        {data.daily_favorites_last_7d.length === 0 ? (
          <div className="py-6 text-center text-xs text-slate-400">无数据</div>
        ) : (
          <div className="space-y-1">
            {data.daily_favorites_last_7d.map((d) => (
              <div key={d.day} className="flex items-center gap-2 text-xs">
                <div className="w-20 text-slate-500">{d.day}</div>
                <div className="flex flex-1 items-center gap-2">
                  <div
                    className="h-4 rounded bg-sky-400"
                    style={{ width: `${(d.count / maxDaily) * 100}%` }}
                  />
                  <span className="font-mono tabular-nums">{d.count}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="mb-3 text-sm font-semibold">收藏消息类型分布</h3>
        {Object.keys(data.msg_type_distribution).length === 0 ? (
          <div className="py-6 text-center text-xs text-slate-400">无数据</div>
        ) : (
          <div className="space-y-1">
            {Object.entries(data.msg_type_distribution).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2 text-xs">
                <div className="w-20 text-slate-500">{k}</div>
                <div className="flex flex-1 items-center gap-2">
                  <div
                    className="h-4 rounded bg-emerald-400"
                    style={{ width: `${(v / maxMsg) * 100}%` }}
                  />
                  <span className="font-mono tabular-nums">{v}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Counter({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 text-3xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}
